import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { GameRenderer } from '../components/placement';

// ─── Palette ──────────────────────────────────────────────────────────────
const PALETTES = {
  B: { key: 'B', primary: '#FF5C5C', primaryShadow: '#B83838', accent: '#FFD23F', accent2: '#3DC9B5', coral: '#FF5C5C', plum: '#5B2E5E', aqua: '#3DC9B5', ink: '#2A1F3D', cream: '#FFF4E6', sand: '#FFE4C4', mutedText: '#7A6B85', heroStart: '#FFB86B', heroEnd: '#FF5C5C' },
  A: { key: 'A', primary: '#2A6B30', primaryShadow: '#1E5C2E', accent: '#E8A020', accent2: '#67A844', coral: '#2A6B30', plum: '#1E5C2E', aqua: '#67A844', ink: '#3E2B1E', cream: '#F8F6F0', sand: '#F0EAD6', mutedText: '#A67C52', heroStart: '#EAF2EA', heroEnd: '#1E5C2E' },
  C: { key: 'C', primary: '#5B4EC7', primaryShadow: '#3B2F9E', accent: '#FFD23F', accent2: '#16A085', coral: '#5B4EC7', plum: '#3B2F9E', aqua: '#16A085', ink: '#1A1A2E', cream: '#F5F3FF', sand: '#EEE9FF', mutedText: '#6A6A80', heroStart: '#B8A9FF', heroEnd: '#5B4EC7' },
};
const FONT = '"Nunito", "Baloo 2", sans-serif';
const Owl = ({ size = 30 }) => <div style={{ fontSize: size * 0.85, lineHeight: 1 }}>🦉</div>;

// v1 level meta
const LEVEL_META_V1 = {
  1: { label: 'DỄ · STARTERS', color: '#3DC9B5', emoji: '🐣' },
  2: { label: 'VỪA · MOVERS', color: '#FFD23F', emoji: '🐥' },
  3: { label: 'KHÓ · MOVERS+', color: '#FF8A3D', emoji: '🦉' },
  4: { label: 'RẤT KHÓ · FLYERS', color: '#FF5C5C', emoji: '🦅' },
};

// v2 C level meta
const C_LEVEL_META = {
  1: { label: 'STARTERS', color: '#3DC9B5', emoji: '🐣' },
  2: { label: 'MOVERS', color: '#FFD23F', emoji: '🐥' },
  3: { label: 'FLYERS', color: '#FF5C5C', emoji: '🦅' },
};

// v2 G tier meta
const G_TIER_META = {
  1: { label: 'G1', icon: '👂' },
  2: { label: 'G2', icon: '🖼️' },
  3: { label: 'G3', icon: '🔄' },
  4: { label: 'G4', icon: '📖' },
  5: { label: 'G5', icon: '✏️' },
};

const PlacementTest = () => {
  const navigate = useNavigate();
  const P = PALETTES.B;

  const [sessionData, setSessionData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [combo, setCombo] = useState(0);
  const [levelUpToast, setLevelUpToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [roundKey, setRoundKey] = useState(0); // force re-render game components

  // Time tracking
  const roundStartRef = useRef(Date.now());
  useEffect(() => {
    roundStartRef.current = Date.now();
  }, [roundKey]);

  // Detect v2
  const isV2 = sessionData?.c_level != null || sessionData?.round_data?.game_type != null;

  useEffect(() => {
    const raw = sessionStorage.getItem('placement_session');
    if (!raw) {
      navigate('/placement-test-intro');
      return;
    }
    try {
      setSessionData(JSON.parse(raw));
    } catch {
      navigate('/placement-test-intro');
    }
  }, [navigate]);

  const finalize = useCallback(async (sessionId) => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/client/placement-test/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ session_id: sessionId }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        setError(data?.detail || `HTTP ${res.status}`);
        return;
      }
      sessionStorage.setItem('placement_result', JSON.stringify(data));
      sessionStorage.removeItem('placement_session');
      navigate('/placement-result');
    } catch (err) {
      setError(err.message);
    }
  }, [navigate]);

  // ── v2 answer handler (called by GameRenderer) ──
  const handleAnswerV2 = useCallback(async (isCorrect, selectedLabel) => {
    if (!sessionData || loading) return;
    const responseTimeMs = Date.now() - roundStartRef.current;
    setCombo(isCorrect ? combo + 1 : 0);
    setLoading(true);

    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/client/placement-test/answer`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            session_id: sessionData.session_id,
            is_correct: isCorrect,
            selected_label: selectedLabel,
            response_time_ms: responseTimeMs,
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        setError(data?.detail || `HTTP ${res.status}`);
        setLoading(false);
        return;
      }

      if (data.next === 'done') {
        await finalize(sessionData.session_id);
        return;
      }

      // Level up toast
      if (data.c_level && data.c_level > (sessionData.c_level || 1)) {
        setLevelUpToast(data.c_level);
        setTimeout(() => setLevelUpToast(null), 2000);
      }

      // Small delay then load next round
      setTimeout(() => {
        setSessionData({
          ...sessionData,
          round_data: data.round_data,
          round_idx: data.round_idx,
          c_level: data.c_level ?? sessionData.c_level,
          g_tier: data.g_tier ?? sessionData.g_tier,
          current_level: data.current_level ?? sessionData.current_level,
        });
        setRoundKey(k => k + 1);
        setLoading(false);
      }, 400);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, [sessionData, loading, combo, finalize]);

  // ── v1 answer handler (legacy MCQ) ──
  const handleAnswerV1 = async (optIndex) => {
    if (showResult || !sessionData) return;
    const opt = sessionData.round_data.options[optIndex];
    const isCorrect = !!opt.correct;
    setSelected(optIndex);
    setShowResult(true);
    setCombo(isCorrect ? combo + 1 : 0);
    setLoading(true);

    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/client/placement-test/answer`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            session_id: sessionData.session_id,
            is_correct: isCorrect,
            selected_label: opt.label,
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) { setError(data?.detail || `HTTP ${res.status}`); setLoading(false); return; }

      setTimeout(async () => {
        if (data.next === 'done') { await finalize(sessionData.session_id); return; }
        if (data.current_level && data.current_level > sessionData.current_level) {
          setLevelUpToast(data.current_level);
          setTimeout(() => setLevelUpToast(null), 1800);
        }
        setSessionData({
          ...sessionData, round_data: data.round_data, round_idx: data.round_idx,
          current_level: data.current_level ?? sessionData.current_level,
        });
        setSelected(null); setShowResult(false); setLoading(false);
      }, isCorrect ? 900 : 1400);
    } catch (err) { setError(err.message); setLoading(false); }
  };

  const handleStop = async () => {
    if (!sessionData) return;
    await finalize(sessionData.session_id);
  };

  if (!sessionData) {
    return (
      <div style={{ minHeight: '100vh', background: P.cream, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT, color: P.ink, fontWeight: 700 }}>
        Đang tải...
      </div>
    );
  }

  const round = sessionData.round_data;
  const roundNumber = (sessionData.round_idx || 0) + 1;
  const total = sessionData.total_rounds || 12;

  // Level/tier display
  const cMeta = isV2 ? (C_LEVEL_META[sessionData.c_level] || C_LEVEL_META[1]) : (LEVEL_META_V1[sessionData.current_level] || LEVEL_META_V1[1]);
  const gMeta = isV2 ? (G_TIER_META[sessionData.g_tier] || G_TIER_META[1]) : null;

  const TopBar = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 28px', background: '#fff', borderBottom: `3px solid ${P.ink}`, flexWrap: 'wrap' }}>
      <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#fff', border: `2.5px solid ${P.ink}`, boxShadow: `0 3px 0 ${P.ink}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Owl size={30} />
      </div>
      <div style={{ fontSize: 18, fontWeight: 800, color: P.ink }}>HuPath</div>
      <div style={{ width: 1, height: 22, background: P.ink, opacity: 0.2 }} />
      <div style={{ background: P.plum, color: '#fff', padding: '6px 14px', border: `2.5px solid ${P.ink}`, boxShadow: `0 3px 0 ${P.ink}`, borderRadius: 10, fontSize: 12, fontWeight: 800, letterSpacing: 1.5 }}>🎯 PLACEMENT</div>
      <div style={{ fontSize: 14, fontWeight: 800 }}>Câu {roundNumber}/{total}</div>

      {/* C level badge */}
      <div style={{
        background: cMeta.color, color: P.ink,
        border: `2.5px solid ${P.ink}`, boxShadow: `0 2px 0 ${P.ink}`,
        padding: '4px 12px', borderRadius: 8,
        fontSize: 11, fontWeight: 800, letterSpacing: 1,
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        {cMeta.emoji} {cMeta.label}
      </div>

      {/* G tier badge (v2 only) */}
      {gMeta && (
        <div style={{
          background: P.sand, color: P.ink,
          border: `2px solid ${P.ink}`, boxShadow: `0 2px 0 ${P.ink}`,
          padding: '4px 10px', borderRadius: 8,
          fontSize: 11, fontWeight: 800,
          display: 'flex', alignItems: 'center', gap: 4,
        }}>
          {gMeta.icon} {gMeta.label}
        </div>
      )}

      <div style={{ flex: 1 }} />
      {combo >= 2 && (
        <div style={{ fontSize: 12, fontWeight: 800, color: P.coral }}>🔥 {combo} combo!</div>
      )}
      <button onClick={() => setShowSkipModal(true)} style={{
        padding: '6px 12px', border: `2px solid ${P.ink}`, borderRadius: 8,
        background: P.coral, color: '#fff', fontFamily: FONT, fontSize: 11, fontWeight: 800,
        boxShadow: `0 2px 0 ${P.primaryShadow}`, cursor: 'pointer',
      }}>⏹ Dừng</button>
    </div>
  );

  const progressBar = (
    <div style={{ padding: '10px 28px', background: '#fff', borderBottom: `2px solid ${P.ink}` }}>
      <div style={{ height: 10, background: P.cream, border: `2px solid ${P.ink}`, borderRadius: 99, overflow: 'hidden', padding: 2 }}>
        <div style={{
          width: `${((roundNumber - 1) / total) * 100}%`, height: '100%',
          background: `linear-gradient(90deg, ${P.aqua}, ${P.accent}, ${P.coral})`,
          borderRadius: 99, transition: 'width 0.4s',
        }} />
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: P.cream, fontFamily: FONT, color: P.ink, display: 'flex', flexDirection: 'column' }}>
      {TopBar}
      {progressBar}

      {levelUpToast && (
        <div style={{
          position: 'fixed', top: 100, left: '50%', transform: 'translateX(-50%)',
          background: P.accent, color: P.ink,
          border: `3px solid ${P.ink}`, boxShadow: `0 6px 0 ${P.ink}`,
          borderRadius: 16, padding: '14px 24px',
          fontSize: 18, fontWeight: 800, zIndex: 1000,
        }}>
          {isV2 ? `🔥 Level Up! ${C_LEVEL_META[levelUpToast]?.emoji || ''} ${C_LEVEL_META[levelUpToast]?.label || ''}` : '🔥 Giỏi quá! Hu cho khó hơn chút nhé!'}
        </div>
      )}

      <div style={{ flex: 1, padding: '40px 32px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        {isV2 ? (
          /* ── v2: Render via GameRenderer ── */
          <GameRenderer
            key={roundKey}
            roundData={round}
            onAnswer={handleAnswerV2}
            palette={P}
            disabled={loading}
          />
        ) : (
          /* ── v1: Legacy MCQ ── */
          <>
            <div style={{ fontSize: 13, fontWeight: 800, color: P.plum, letterSpacing: 2, textAlign: 'center' }}>CHỌN TỪ TIẾNG ANH ĐÚNG</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: P.mutedText, marginTop: 6 }}>Nghĩa tiếng Việt:</div>
            <div style={{
              marginTop: 14, background: '#fff', border: `3px solid ${P.ink}`,
              boxShadow: `0 6px 0 ${P.ink}`, borderRadius: 20,
              padding: '28px 40px', maxWidth: 520, width: '100%', textAlign: 'center',
            }}>
              <div style={{ fontSize: 36, fontWeight: 800, color: P.ink }}>{round.prompt.word_vi || '...'}</div>
              {round.prompt.pronunciation_uk && sessionData.current_level <= 2 && (
                <div style={{ fontSize: 14, fontWeight: 600, color: P.mutedText, marginTop: 6, fontFamily: 'monospace' }}>{round.prompt.pronunciation_uk}</div>
              )}
            </div>
            <div style={{ marginTop: 30, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, maxWidth: 560, width: '100%' }}>
              {round.options.map((opt, idx) => {
                const isSelected = selected === idx;
                const showGreen = showResult && opt.correct;
                const showRed = showResult && isSelected && !opt.correct;
                const letter = String.fromCharCode(65 + idx);
                return (
                  <button key={idx} onClick={() => handleAnswerV1(idx)} disabled={showResult || loading}
                    style={{
                      padding: '18px 16px', border: `3px solid ${P.ink}`, borderRadius: 14,
                      background: showGreen ? P.aqua : (showRed ? '#FFB8B8' : (isSelected ? P.accent : '#fff')),
                      color: showGreen ? '#fff' : P.ink, fontFamily: FONT, fontSize: 16, fontWeight: 800,
                      boxShadow: `0 ${(showGreen || showRed || isSelected) ? 3 : 5}px 0 ${P.ink}`,
                      cursor: (showResult || loading) ? 'default' : 'pointer',
                      display: 'flex', alignItems: 'center', gap: 12,
                      transform: (showGreen || showRed || isSelected) ? 'translateY(2px)' : 'translateY(0)',
                      transition: 'all 0.12s',
                    }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: 8, background: showGreen ? '#fff' : P.accent,
                      border: `2px solid ${P.ink}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 14, fontWeight: 800, color: P.ink, flexShrink: 0,
                    }}>{letter}</div>
                    <div style={{ flex: 1, textAlign: 'left' }}>{opt.label}</div>
                    {showGreen && <div style={{ fontSize: 22 }}>✓</div>}
                    {showRed && <div style={{ fontSize: 22 }}>✗</div>}
                  </button>
                );
              })}
            </div>
            {showResult && (
              <div style={{
                marginTop: 18, padding: '8px 16px',
                background: round.options[selected]?.correct ? P.aqua : P.sand,
                border: `2px solid ${P.ink}`, borderRadius: 10, fontSize: 13, fontWeight: 800,
                color: round.options[selected]?.correct ? '#fff' : P.ink,
              }}>
                {round.options[selected]?.correct ? '🎉 Đúng rồi!' : 'Chưa đúng nhé — tiếp tục thôi!'}
              </div>
            )}
          </>
        )}

        <div style={{ marginTop: 24, fontSize: 12, fontWeight: 700, color: P.mutedText, textAlign: 'center' }}>
          💡 Không biết cứ đoán — Hu không trừ điểm nhé!
        </div>
      </div>

      {error && (
        <div style={{ padding: '10px 20px', background: '#FEE', borderTop: `2px solid ${P.coral}`, fontSize: 13, color: P.coral, textAlign: 'center' }}>
          Lỗi: {error}
        </div>
      )}

      {showSkipModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(42,31,61,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 22, padding: '28px 30px', maxWidth: 440, width: '100%', border: `3.5px solid ${P.ink}`, boxShadow: `0 10px 0 ${P.ink}` }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: P.plum, letterSpacing: 2 }}>DỪNG BÀI KIỂM TRA?</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: P.ink, marginTop: 4 }}>Xem kết quả ngay?</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: P.mutedText, marginTop: 10, lineHeight: 1.5 }}>
              Hu sẽ tính trình độ dựa trên {roundNumber - 1} câu đã làm.
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button onClick={() => setShowSkipModal(false)} style={{
                flex: 1, padding: '12px', border: `2.5px solid ${P.ink}`, background: '#fff', color: P.ink,
                fontFamily: FONT, fontSize: 13, fontWeight: 800, borderRadius: 12, boxShadow: `0 4px 0 ${P.ink}`, cursor: 'pointer',
              }}>Tiếp tục làm</button>
              <button onClick={handleStop} style={{
                flex: 1.2, padding: '12px', border: `2.5px solid ${P.ink}`, background: P.coral, color: '#fff',
                fontFamily: FONT, fontSize: 13, fontWeight: 800, borderRadius: 12, boxShadow: `0 4px 0 ${P.primaryShadow}`, cursor: 'pointer',
              }}>Xem kết quả</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlacementTest;
